import { useEffect, useMemo, useState } from 'react';
import { ENTRIES_POLL_INTERVAL, RUN_TASK_WIDGET_ID, RunTaskEntryType, RunTaskWidgetAction, RunTaskWidgetSideEffect } from '../constants';
import { fetchEntries, getVisibleEntries, scrollIntoViewIfNeeded, shouldTriggerWidgetUpdate, updateWidgetInConversation } from '../utils';
import { useTranslation } from './useTranslation';
export function useEntries(_ref) {
  let {
    data,
    context,
    config,
    isStopping,
    isEditingApproval,
    hasTimedOut,
    resolvedApprovalIds,
    widgetRef
  } = _ref;
  const {
    t,
    isLoading: isTranslationsLoading
  } = useTranslation();
  const startDate = useMemo(() => new Date(), []);
  const [invocationId, setInvocationId] = useState(data.invocationId);
  const [entries, setEntries] = useState(() => data.entries ?? []);
  const [pollCounter, setPollCounter] = useState(1);
  const [shouldAlwaysFetchApprovals, setShouldAlwaysFetchApprovals] = useState(false);
  const {
    onWidgetAction,
    emitSideEffect
  } = context;
  const httpClient = context.httpClient;
  const {
    setCanSend,
    messageIndex,
    messagesCount
  } = config;
  const {
    isRestoring,
    automation,
    conversationId
  } = data;

  // Only when widget is the last message in the conversation
  const shouldManageChatInputAndScroll = !!conversationId && typeof messageIndex === 'number' && typeof messagesCount === 'number' && messageIndex === messagesCount - 1;
  const shouldUpdateWidgetInConversation = !!conversationId;

  // Avoid fetching/polling when:
  // - The widget is in common chat and has completed to the end (data.entries are present)
  // - The edit approval modal is open
  const shouldFetchLatestEntries = !data.entries && !isEditingApproval;
  const refetchLatestEntries = () => {
    if (!shouldFetchLatestEntries) {
      return;
    }
    setPollCounter(prevPollCounter => prevPollCounter + 1);
  };
  const fetchLatestEntries = async () => {
    const {
      entries: newEntries,
      invocationId: newInvocationId,
      isPending,
      shouldAlwaysFetchApprovals: newShouldAlwaysFetchApprovals
    } = await fetchEntries({
      httpClient,
      invocationId,
      automation,
      shouldAlwaysFetchApprovals,
      t
    });
    setEntries(newEntries);
    setInvocationId(newInvocationId);
    setShouldAlwaysFetchApprovals(newShouldAlwaysFetchApprovals);
    if (isPending) {
      setTimeout(refetchLatestEntries, ENTRIES_POLL_INTERVAL);
    } else {
      onWidgetAction(RUN_TASK_WIDGET_ID, {
        action: RunTaskWidgetAction.Completed
      });
      emitSideEffect(RunTaskWidgetSideEffect.Completed, {
        invocationId: newInvocationId
      });
      if (shouldManageChatInputAndScroll) {
        setCanSend == null || setCanSend(true);
      }
      if (shouldUpdateWidgetInConversation) {
        updateWidgetInConversation({
          conversationId,
          invocationId: newInvocationId,
          data,
          config,
          entries: newEntries,
          httpClient
        });
      }
    }
    if (shouldTriggerWidgetUpdate(newEntries, entries)) {
      onWidgetAction(RUN_TASK_WIDGET_ID, {
        action: RunTaskWidgetAction.Updated
      });
      if (shouldManageChatInputAndScroll && widgetRef.current) {
        scrollIntoViewIfNeeded(widgetRef.current);
      }
    }
  };
  useEffect(() => {
    if (shouldFetchLatestEntries) {
      fetchLatestEntries();
    }
  }, [pollCounter, shouldFetchLatestEntries]);
  useEffect(() => {
    // Last message and not completed yet
    if (shouldManageChatInputAndScroll && !entries.length) {
      setCanSend == null || setCanSend(false);
    }
  }, []);
  const visibleEntries = useMemo(() => getVisibleEntries({
    invocationId,
    automation,
    isRestoring,
    entries,
    startDate,
    isStopping,
    isTranslationsLoading,
    resolvedApprovalIds,
    hasTimedOut,
    t
  }), [entries, invocationId, automation, isRestoring, startDate, isStopping, isTranslationsLoading, resolvedApprovalIds, hasTimedOut, t]);
  const hasCompletedInvocation = useMemo(() => entries.some(entry => entry.type === RunTaskEntryType.End), [entries]);
  return {
    entries: visibleEntries,
    hasCompletedInvocation,
    invocationId
  };
}
//# sourceMappingURL=useEntries.js.map